/**
 * Helpers for dealing with the signed upload URLs returned by
 * `ApiService.uploadUrlsCreate` and the public download URLs derived from them.
 *
 * The bucket and file_name are taken from the `/api/upload-urls/` response, NOT
 * parsed from the upload URL: the same-origin upload path rewrites the upload URL
 * to the app host (e.g. lumen5.com/uploads/...), so its host is not the bucket.
 * The only thing we parse out of the upload URL is the (sanitized) file name in
 * its path, which is unaffected by the host rewrite.
 */

/**
 * Extracts the file name (last, URI-decoded path segment) from a signed upload URL.
 * Does NOT derive the bucket — the bucket comes from the upload-urls response.
 */
export function getFileNameFromUploadUrl(uploadUrl: string): string {
    const url = new URL(uploadUrl);
    const lastSegment = url.pathname
        .split('/')
        .filter((segment) => segment)
        .pop();
    return decodeURIComponent(lastSegment ?? '');
}

/**
 * Builds the public GCS download URL from a bucket and file_name pair.
 *
 * Always build from the bucket + file_name the `/api/upload-urls/` response exposes
 * — NOT from the upload URL's host. The same-origin upload path rewrites the upload
 * URL to the app host (e.g. lumen5.com/uploads/...), so its host is not the bucket;
 * deriving the bucket from it produces a broken URL (e.g. .../localhost:8100/...).
 */
export function buildPublicGcsUrl({
    bucket,
    file_name,
}: {
    bucket: string;
    file_name: string;
}): string {
    return `https://storage.googleapis.com/${bucket}/${file_name}`;
}
